import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import { HelpPopover } from '@/design';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { TAG_LIMITS, type IMailTagCount, type MailUsage } from '../../api/mail-template-contracts';
import { useAdminData } from '../../components/admin-page';

/**
 * A template's tags and usages (SR-523 stage 5). Tags only label mail — a template, the campaigns
 * made from it, the Email log's rows — and change nothing else. Usages say where a custom template
 * can be chosen: the Lists send dialog, a cycle's reminder steps.
 */

export const USAGES: readonly { value: MailUsage; label: string; short: string }[] = [
  { value: 'lists', label: 'Email from Lists', short: 'Lists' },
  { value: 'reminders', label: 'Cycle reminders and invitations', short: 'Reminders' }
];

/** Why survey-api would refuse a tag, or null. */
export function tagProblem(tag: string): Nullable<string> {
  const trimmed = tag.trim();
  if (!trimmed) return null;
  if (trimmed.includes(',')) return 'No commas in a tag.';
  if (trimmed.length > TAG_LIMITS.length) return `At most ${TAG_LIMITS.length} characters.`;
  return null;
}

function hasTag(tags: readonly string[], tag: string): boolean {
  const wanted = tag.trim().toLowerCase();
  return tags.some((each) => each.toLowerCase() === wanted);
}

/** The preset tags and the ones in use; a failed read offers none. */
export function useMailTags(): IMailTagCount[] {
  const read = useAdminData((signal) => mailTemplatesApi.tags(signal), []);
  return read.data ?? [];
}

/** A row's tags, small and quiet; past `max`, the rest counted. */
export function TagChips({ tags, max, className }: { tags: readonly string[]; max?: number; className?: string }) {
  if (tags.length === 0) return null;
  const shown = max ? tags.slice(0, max) : tags;
  const more = tags.length - shown.length;
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)} data-tags="">
      {shown.map((tag) => (
        <span
          key={tag}
          className="inline-flex max-w-48 items-center truncate rounded-full border border-[color-mix(in_srgb,var(--color-teal)_30%,white)] bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)] px-1.5 text-[0.6875rem] leading-[1.125rem] text-[var(--color-royal-blue)]"
          data-tag={tag}
        >
          {tag}
        </span>
      ))}
      {more > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              role="img"
              aria-label={`${more} more: ${tags.slice(shown.length).join(', ')}`}
              className="rounded-sm text-[0.6875rem] text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-tags-more={more}
            >
              +{more}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">{tags.join(', ')}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

/** A custom template's usages, as small chips. */
export function UsageChips({ usages }: { usages: readonly MailUsage[] }) {
  if (usages.length === 0) return null;
  return (
    <span className="whitespace-nowrap text-xs text-muted-foreground" data-usages="">
      Used in{' '}
      {USAGES.filter((usage) => usages.includes(usage.value))
        .map((usage) => usage.short)
        .join(' · ')}
    </span>
  );
}

export function TagsHelp() {
  return (
    <HelpPopover label="About tags" className="size-6">
      <p>
        Tags only label mail: the template, the campaigns sent with it and their emails in the
        Email log, which filters by them. They change nothing else — no contact attempt is
        recorded and no list field moves.
      </p>
      <p>
        Up to {TAG_LIMITS.count}, each at most {TAG_LIMITS.length} characters and no commas. Pick a
        suggestion or type your own and press Enter.
      </p>
    </HelpPopover>
  );
}

/** Chips and a line to type a tag into; the suggestions — presets first, then those in use —
 *  under it, narrowed by what is typed. */
export function TagInput({
  id,
  labelledBy,
  value,
  onChange,
  suggestions
}: {
  id: string;
  labelledBy: string;
  value: readonly string[];
  onChange: (tags: string[]) => void;
  suggestions: readonly IMailTagCount[];
}) {
  const [draft, setDraft] = React.useState('');
  const full = value.length >= TAG_LIMITS.count;
  const problem = tagProblem(draft);
  const typed = draft.trim().toLowerCase();
  const offered = suggestions
    .filter((entry) => !hasTag(value, entry.tag) && (!typed || entry.tag.toLowerCase().includes(typed)))
    .slice(0, 8);

  const add = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || full || tagProblem(trimmed)) return;
    if (!hasTag(value, trimmed)) onChange([...value, trimmed]);
    setDraft('');
  };

  return (
    <div className="grid gap-1.5">
      <div
        className={cn(
          'flex min-h-8 flex-wrap items-center gap-1.5 rounded-md border border-input bg-white px-2 py-1',
          'has-[:focus]:border-ring has-[:focus]:ring-[3px] has-[:focus]:ring-ring/50',
          problem && 'border-destructive'
        )}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-[color-mix(in_srgb,var(--color-turquoise)_12%,white)] py-0.5 pr-1 pl-2 text-xs text-[var(--color-royal-blue)]"
            data-tag-chip={tag}
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove the tag ${tag}`}
              onClick={() => onChange(value.filter((each) => each !== tag))}
              className="rounded-full p-0.5 outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}
        {!full && (
          <input
            id={id}
            value={draft}
            maxLength={TAG_LIMITS.length + 10}
            placeholder={value.length ? '' : 'Type a tag, then Enter'}
            aria-labelledby={labelledBy}
            aria-invalid={problem ? true : undefined}
            aria-describedby={problem ? `${id}-problem` : undefined}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => add(draft)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                add(draft);
              } else if (event.key === 'Backspace' && !draft && value.length) {
                onChange(value.slice(0, -1));
              }
            }}
            className="h-6 min-w-32 flex-1 bg-transparent text-[0.8125rem] outline-none"
            data-tag-input=""
          />
        )}
      </div>
      {problem && (
        <p id={`${id}-problem`} className="text-xs text-destructive">
          {problem}
        </p>
      )}
      {full && <p className="text-xs text-muted-foreground">{TAG_LIMITS.count} tags, the most a template takes.</p>}
      {!full && offered.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Suggested tags">
          {offered.map((entry) => (
            <button
              key={entry.tag}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => add(entry.tag)}
              title={entry.preset ? 'A suggested tag' : `On ${entry.templates} template${entry.templates === 1 ? '' : 's'}`}
              className="rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground outline-none hover:border-[var(--color-teal)] hover:text-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring"
              data-tag-suggestion={entry.tag}
            >
              + {entry.tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A tag chip as the template's header draws it, × removing it. */
const HEADER_CHIP =
  'inline-flex h-6 items-center gap-1 rounded-full border border-[color-mix(in_srgb,var(--color-teal)_30%,white)] ' +
  'bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)] pl-2 text-xs text-[var(--color-royal-blue)]';

/**
 * The template's tags under its name (SR-523 stage 6): a chip each with its ×, then *+ Add tag*,
 * which opens a line to type into with the suggestions under it — presets first, then those in
 * use — narrowed by what is typed; arrows pick one, Enter adds it (or what is typed), Escape
 * leaves. Every change is saved at once (`onSave`); a refusal puts the tags back as they were.
 */
export function HeaderTags({
  tags,
  editable,
  onSave
}: {
  tags: readonly string[];
  editable: boolean;
  /** The tags as they should be; resolves null when saved, else why not. */
  onSave: (tags: string[]) => Promise<Nullable<string>>;
}) {
  const id = React.useId();
  const suggestions = useMailTags();
  const [shown, setShown] = React.useState<readonly string[]>(tags);
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [active, setActive] = React.useState(-1);
  const [saving, setSaving] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const addButton = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => setShown(tags), [tags]);

  const full = shown.length >= TAG_LIMITS.count;
  const problem = tagProblem(draft);
  const typed = draft.trim().toLowerCase();
  const offered = suggestions
    .filter((entry) => !hasTag(shown, entry.tag) && (!typed || entry.tag.toLowerCase().includes(typed)))
    .slice(0, 8);

  const save = async (next: string[]) => {
    const before = shown;
    setShown(next);
    setSaving(true);
    setRefusal(null);
    const refused = await onSave(next);
    setSaving(false);
    if (refused) {
      setShown(before);
      setRefusal(refused);
    }
  };

  const close = (refocus: boolean) => {
    setAdding(false);
    setDraft('');
    setActive(-1);
    if (refocus) window.setTimeout(() => addButton.current?.focus(), 0);
  };

  const add = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || tagProblem(trimmed) || full) return;
    close(true);
    if (!hasTag(shown, trimmed)) void save([...shown, trimmed]);
  };

  const remove = (tag: string) => void save(shown.filter((each) => each !== tag));

  const listId = `${id}-suggestions`;
  const optionId = (at: number) => `${id}-suggestion-${at}`;

  return (
    <div className="grid gap-1" data-header-tags="">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Tags" aria-busy={saving || undefined}>
        {shown.map((tag) => (
          <span key={tag} className={cn(HEADER_CHIP, !editable && 'pr-2')} data-tag={tag}>
            {tag}
            {editable && (
              <button
                type="button"
                aria-label={`Remove the tag ${tag}`}
                disabled={saving}
                onClick={() => remove(tag)}
                className="mr-0.5 rounded-full p-0.5 outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-ring"
                data-remove-tag={tag}
              >
                <X className="size-3" aria-hidden />
              </button>
            )}
          </span>
        ))}
        {editable && !full && !adding && (
          <button
            ref={addButton}
            type="button"
            disabled={saving}
            onClick={() => {
              setAdding(true);
              setRefusal(null);
            }}
            className={cn(
              'inline-flex h-6 items-center gap-1 rounded-full border border-dashed border-border px-2 text-xs',
              'text-muted-foreground outline-none hover:border-[var(--color-teal)] hover:text-[var(--color-royal-blue)]',
              'focus-visible:ring-2 focus-visible:ring-ring'
            )}
            data-add-tag=""
          >
            <Plus className="size-3" aria-hidden />
            Add tag
          </button>
        )}
        {adding && (
          <span className="relative">
            <input
              autoFocus
              id={`${id}-input`}
              value={draft}
              maxLength={TAG_LIMITS.length + 10}
              placeholder="Tag, then Enter"
              role="combobox"
              aria-label="New tag"
              aria-expanded={offered.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={active >= 0 && active < offered.length ? optionId(active) : undefined}
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? `${id}-problem` : undefined}
              onChange={(event) => {
                setDraft(event.target.value);
                setActive(-1);
              }}
              onBlur={() => close(false)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  event.stopPropagation();
                  close(true);
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  add(active >= 0 && active < offered.length ? offered[active].tag : draft);
                } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                  if (offered.length === 0) return;
                  event.preventDefault();
                  const step = event.key === 'ArrowDown' ? 1 : -1;
                  // -1 is the typed text; past either end the choice goes round.
                  setActive((was) => {
                    const next = was + step;
                    return next < -1 ? offered.length - 1 : next >= offered.length ? -1 : next;
                  });
                }
              }}
              className={cn(
                'h-6 w-44 rounded-full border border-ring bg-white px-2.5 text-xs outline-none ring-[3px] ring-ring/40',
                problem && 'border-destructive ring-destructive/30'
              )}
              data-tag-input=""
            />
            {offered.length > 0 && (
              <ul
                id={listId}
                role="listbox"
                aria-label="Suggested tags"
                className="absolute top-full left-0 z-50 mt-1 grid max-h-64 w-60 overflow-y-auto rounded-md border border-border bg-popover p-1 text-xs shadow-md"
              >
                {offered.map((entry, at) => (
                  <li
                    key={entry.tag}
                    id={optionId(at)}
                    role="option"
                    aria-selected={at === active}
                    aria-labelledby={`${optionId(at)}-label`}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(at)}
                    onClick={() => add(entry.tag)}
                    className={cn(
                      'flex cursor-pointer items-baseline gap-2 rounded px-2 py-1',
                      at === active && 'bg-[color-mix(in_srgb,var(--color-turquoise)_12%,white)]'
                    )}
                    data-tag-suggestion={entry.tag}
                  >
                    <span id={`${optionId(at)}-label`} className="truncate text-[var(--color-royal-blue)]">
                      {entry.tag}
                    </span>
                    <span className="ml-auto shrink-0 text-muted-foreground">
                      {entry.preset && entry.templates === 0 ? 'suggested' : `${entry.templates}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </span>
        )}
        {editable && <TagsHelp />}
        {saving && (
          <span role="status" className="text-xs text-muted-foreground">
            Saving…
          </span>
        )}
      </div>
      {problem && adding && (
        <p id={`${id}-problem`} className="text-xs text-destructive">
          {problem}
        </p>
      )}
      {refusal && (
        <p role="alert" className="text-xs text-destructive">
          {refusal}
        </p>
      )}
    </div>
  );
}
